import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cn } from 'cn';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { IconRail } from '@/features/reader/IconRail';
import { DropZone } from './DropZone';
import { ExtractionSummary } from './ExtractionSummary';
import { UploadNotice } from './UploadNotice';
import { FridayCalendar } from './FridayCalendar';
import { Icon, StatusPill } from './uploadUi';
import { formatBytes, formatIcon } from './uploadFormat';
import { comingFriday, formatFriday, nearestFriday } from './fridays';
import { UploadFailed, uploadKhutbah } from './uploadApi';
import { severityOf } from './uploadTypes';
import type { ExtractionResult, Publisher, UploadError } from './uploadTypes';

/**
 * Upload a khutbah.
 *
 * The entry point for everything else: until a khutbah is in the database
 * there is nothing to translate, review, read or ask about.
 *
 * Three cards in reading order — the file, the details, and what the file
 * produced — because that is the order the decisions happen in. Extraction is
 * synchronous and its result is shown *before* generation is offered, so the
 * editor spends the AI budget on a khutbah they have actually looked at.
 *
 * ## Why the details are locked after a file is chosen
 *
 * Publisher and Friday are sent *with* the upload, and the server creates the
 * khutbah from them in the same request. There is no endpoint that edits them
 * afterwards, so offering controls that appear to would be a lie the interface
 * tells about the API. They become a read-only summary instead, with the way
 * to change them — start again with the same file — stated plainly.
 */

const PUBLISHERS: { value: Publisher; label: string; dot: string }[] = [
  { value: 'oman-awqaf', label: 'Oman Awqaf', dot: 'bg-institution-oman' },
  { value: 'uae-awqaf', label: 'UAE Awqaf', dot: 'bg-institution-uae' },
  { value: 'none', label: 'No publisher — my own upload', dot: 'bg-status-user' },
];

function publisherLabel(publisher: Publisher): string {
  return PUBLISHERS.find((entry) => entry.value === publisher)?.label ?? publisher;
}

/** A labelled control with its hint. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium">{label}</span>
      {children}
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

const INPUT_CLASSES = cn(
  'h-11 w-full rounded-lg border border-border bg-background px-3 text-sm',
  'outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
  'disabled:opacity-60',
);

function DetailsCard({
  publisher,
  friday,
  onChange,
  locked,
}: {
  publisher: Publisher;
  friday: string;
  onChange: (next: { publisher: Publisher; friday: string }) => void;
  locked: boolean;
}) {
  if (locked) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Details</h2>
          </CardTitle>
          <CardDescription>
            Recorded with the khutbah when it was created. Changing them means
            removing the file and uploading it again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">Publisher</dt>
              <dd className="mt-0.5 text-sm font-medium">
                {publisherLabel(publisher)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Friday</dt>
              <dd className="mt-0.5 text-sm font-medium">{formatFriday(friday)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Source language</dt>
              <dd className="mt-0.5 text-sm font-medium">Arabic</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Details</h2>
        </CardTitle>
        <CardDescription>Who issued it and which Friday it is for</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-[1fr_1.35fr_1fr]">
        <Field label="Publisher" hint="Sets the source badge readers see">
          <div className="relative">
            <i
              aria-hidden
              className={cn(
                'pointer-events-none absolute top-1/2 start-3 size-2 -translate-y-1/2 rounded-full',
                PUBLISHERS.find((entry) => entry.value === publisher)?.dot,
              )}
            />
            <select
              aria-label="Publisher"
              value={publisher}
              onChange={(event) =>
                onChange({ publisher: event.target.value as Publisher, friday })
              }
              className={cn(INPUT_CLASSES, 'appearance-none ps-7 pe-9')}
            >
              {PUBLISHERS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
            {/* `appearance-none` is needed for the institution dot to sit
                inside the control, and it takes the native chevron with it —
                so the affordance is drawn back, on the end edge so it follows
                the writing direction. */}
            <Icon
              name="chevron"
              size={16}
              className="pointer-events-none absolute top-1/2 end-3 -translate-y-1/2 text-muted-foreground"
            />
          </div>
        </Field>

        <FridayCalendar
          value={friday}
          onChange={(next) => onChange({ publisher, friday: next })}
        />

        <Field label="Source language" hint="The language the file is written in">
          {/* One option, so it is shown rather than offered. A select with a
              single entry invites a choice that does not exist. */}
          <div
            className={cn(
              INPUT_CLASSES,
              'flex items-center bg-muted text-muted-foreground',
            )}
          >
            Arabic
          </div>
        </Field>
      </CardContent>
    </Card>
  );
}

function ReadingCard() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>
              <h2>Reading the file</h2>
            </CardTitle>
            <CardDescription>
              Detecting the format, splitting passages and finding quotations — a few
              seconds.
            </CardDescription>
          </div>
          <StatusPill tone="processing">Processing</StatusPill>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3" aria-busy>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[0, 1, 2, 3].map((cell) => (
            <Skeleton key={cell} className="h-14" />
          ))}
        </div>
        <Skeleton className="h-24" />
      </CardContent>
    </Card>
  );
}

/** The chosen file, with what it turned out to be. */
function FileRow({
  file,
  result,
  failed,
  needsDecision,
  busy,
  onRemove,
}: {
  file: File;
  result: ExtractionResult | null;
  failed: boolean;
  /** Nothing is wrong with the file; it is waiting on the editor's choice. */
  needsDecision: boolean;
  busy: boolean;
  onRemove: () => void;
}) {
  // What the bytes were, never what the name claimed — and until the server
  // has said, nothing is claimed at all.
  const detected = result ? formatIcon(result.format) : 'txt';
  const meta = [
    result ? result.formatNote : null,
    formatBytes(file.size),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={cn(
        'flex min-h-14 items-center gap-3 rounded-lg border p-3',
        failed ? 'border-destructive bg-destructive/5' : 'border-border',
      )}
    >
      <Icon
        name={failed ? 'alert' : needsDecision ? 'calendar' : detected}
        size={20}
        className={failed ? 'text-destructive' : 'text-muted-foreground'}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{file.name}</span>
        <span className="text-xs text-muted-foreground">{meta}</span>
      </div>
      {busy ? <StatusPill tone="processing">Processing</StatusPill> : null}
      {result ? <StatusPill tone="done">Extracted</StatusPill> : null}
      {failed ? <StatusPill tone="failed">Failed</StatusPill> : null}
      {needsDecision ? <StatusPill tone="queued">Not uploaded</StatusPill> : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-lg"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
      >
        <Icon name="close" size={16} />
      </Button>
    </div>
  );
}

export function UploadScreen() {
  const [publisher, setPublisher] = useState<Publisher>('oman-awqaf');
  const [friday, setFriday] = useState<string>(() => comingFriday());
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<UploadError | null>(null);
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const upload = useMutation({
    // Never retried automatically. An upload is not idempotent from the
    // client's side of the wire: the server may have created the khutbah and
    // only the response was lost, and a blind retry would be a second attempt
    // at something that already succeeded. The content hash makes a *deliberate*
    // retry safe — it links rather than duplicating — but that is the editor's
    // decision to make, not a default.
    retry: false,
    mutationFn: async ({ chosen, replace }: { chosen: File; replace: boolean }) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // Snapped, not trusted: `friday` can only have come from the stepper,
      // but a khutbah filed on a Tuesday is invisible to every reader who
      // looks for it, so the guarantee is made here too rather than assumed.
      return uploadKhutbah(
        chosen,
        publisher,
        nearestFriday(friday),
        controller.signal,
        replace,
      );
    },
    onSuccess: (extracted) => {
      setResult(extracted);
      setError(null);
    },
    onError: (cause) => {
      setResult(null);
      setError(
        cause instanceof UploadFailed
          ? cause.error
          : {
              kind: 'network',
              message:
                cause instanceof Error
                  ? cause.message
                  : 'The upload could not be completed.',
            },
      );
    },
  });

  function pick(chosen: File) {
    setFile(chosen);
    setError(null);
    setResult(null);
    upload.mutate({ chosen, replace: false });
  }

  function clear() {
    abortRef.current?.abort();
    setFile(null);
    setError(null);
    setResult(null);
    upload.reset();
  }

  function openKhutbah(khutbahId: string) {
    window.location.search = `?khutbah=${encodeURIComponent(khutbahId)}`;
  }

  const busy = upload.isPending;
  // Derived from severity, not from "is there an error": a Friday that is
  // already taken and a file that was uploaded before are both warnings about
  // the world, not faults in the file — stamping the row "Failed" in red
  // under an amber notice tells the editor two different things at once.
  const failed = error !== null && severityOf(error) === 'destructive';

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="admin" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-border bg-card px-6 py-3">
          <nav aria-label="Breadcrumb" className="flex-1">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground">
              <li>Admin review</li>
              <li aria-hidden>›</li>
              <li className="font-medium text-foreground" aria-current="page">
                Upload a khutbah
              </li>
            </ol>
          </nav>
          <span className="inline-flex items-center gap-2 text-[15px] font-bold tracking-[-0.01em]">
            Almimbar
            <span className="script-ar text-[15px] font-medium leading-none text-primary">
              المنبر
            </span>
          </span>
        </header>

        <main className="flex-1 overflow-auto px-4 pb-12 pt-8 sm:px-6">
          <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-[-0.01em]">
                Upload a khutbah
              </h1>
              <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
                Add the Friday khutbah as the publisher issued it. The text is
                extracted at once; translation and learning content are generated
                after you confirm.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>File</h2>
                </CardTitle>
                <CardDescription>
                  PDF, Word (.doc, .docx) or plain text (.txt, UTF-8) — up to 25 MB and
                  40 pages
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {file === null ? (
                  <DropZone onPick={pick} disabled={busy} />
                ) : (
                  <FileRow
                    file={file}
                    result={result}
                    failed={failed}
                    needsDecision={error !== null && !failed && result === null}
                    busy={busy}
                    onRemove={clear}
                  />
                )}

                {/* Announced, not just drawn: the outcome of an upload arrives
                    while focus is still on the control that started it. */}
                <div aria-live="polite" className="sr-only">
                  {busy ? 'Reading the file.' : null}
                  {result
                    ? `Extracted ${result.passages.length} passages from a ${result.format} file.`
                    : null}
                </div>

                {error ? (
                  <UploadNotice
                    error={error}
                    onChooseAnother={clear}
                    onRetry={
                      // Only a transport failure is worth repeating unchanged.
                      // A refused format or encoding will be refused again.
                      error.kind === 'network' && file
                        ? () => upload.mutate({ chosen: file, replace: false })
                        : undefined
                    }
                    onReplace={
                      // The permission step. Sent only from this press, and
                      // only for a Friday clash the server said is replaceable.
                      error.kind === 'same-friday' && !error.published && file
                        ? () => upload.mutate({ chosen: file, replace: true })
                        : undefined
                    }
                  />
                ) : null}
              </CardContent>
            </Card>

            <DetailsCard
              publisher={publisher}
              friday={friday}
              onChange={(next) => {
                setPublisher(next.publisher);
                setFriday(next.friday);
              }}
              locked={result !== null || busy}
            />

            {busy ? <ReadingCard /> : null}

            {result ? (
              <ExtractionSummary
                result={result}
                onOpen={() => openKhutbah(result.khutbahId)}
              />
            ) : null}

            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Icon name="info" size={14} className="mt-0.5" />
              <span>
                Unofficial reading aid · not endorsed by the publisher. Provenance is
                recorded with the khutbah and shown to every reader.
              </span>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
